'use client';

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import useAuthenticatedUser from '@dr-pam/common-components/Hooks/useAuthenticatedUser';
import { useClientUpgradeService, type ActiveUpgradePrompt } from '../../services/client/ClientUpgradeService';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import SessionStorageUtil from '@dr-pam/common-components/Utils/SessionStorageUtil';
import { shouldShowPromptOnPage, normalizePromptType } from '../../utils/upgradePromptRouteMatcher';

import {
	STORAGE_KEY_PROMPT_DISMISSED,
	STORAGE_KEY_PROMPT_ENGAGED,
	STORAGE_KEY_PROMPT_SHOWN,
	STORAGE_KEY_UPGRADE_ENGAGEMENT_ID,
	STORAGE_KEY_UPGRADE_SESSION_ID,
} from '../../utils/signupStorageKeys';

// Generate session ID for engagement tracking
function generateSessionId(): string {
	if (typeof window === 'undefined') {
		return '';
	}
	const stored = sessionStorage.getItem(STORAGE_KEY_UPGRADE_SESSION_ID);
	if (stored) {
		return stored;
	}
	const newId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
	sessionStorage.setItem(STORAGE_KEY_UPGRADE_SESSION_ID, newId);
	return newId;
}

// Check if prompt should be shown based on frequency rules
function shouldShowPrompt(prompt: ActiveUpgradePrompt, dismissedPrompts: Set<string>): boolean {
	const { frequency } = prompt.prompt;
	const promptId = prompt.prompt.id;

	// Always respect dismissal - if dismissed, never show again
	if (dismissedPrompts.has(promptId)) {
		return false;
	}

	switch (frequency) {
		case 'ONCE': {
			// Show only if never shown before (once per user, ever)
			// Track "shown" state, not just "engaged" - user might dismiss without clicking
			// Check sessionStorage directly to avoid React state update race conditions
			const onceShownKey = `${STORAGE_KEY_PROMPT_SHOWN}${promptId}`;
			const wasShownOnce = SessionStorageUtil.getItem(onceShownKey);
			return !wasShownOnce;
		}

		case 'ONCE_PER_DAY': {
			// Check if shown today (date-based, resets at midnight)
			// Track "shown" not "engaged" - user might see but not click
			const todayKey = `${STORAGE_KEY_PROMPT_SHOWN}${promptId}_${new Date().toDateString()}`;
			const shownToday = SessionStorageUtil.getItem(todayKey);
			return !shownToday;
		}

		case 'ONCE_PER_SESSION': {
			// Check if shown in this session (sessionStorage persists across page reloads)
			// This means "once per browser session" (persists across page reloads until tab closes)
			// Track "shown" not "engaged" - user might see but not click
			// Check sessionStorage directly to avoid React state update race conditions
			const sessionShownKey = `${STORAGE_KEY_PROMPT_SHOWN}${promptId}`;
			const wasShownInSession = SessionStorageUtil.getItem(sessionShownKey);
			return !wasShownInSession;
		}

		case 'EVERY_PAGE_LOAD':
			// Show on every page load unless dismissed
			// Engagement doesn't prevent showing (user can engage multiple times)
			// Works for BANNER, MODAL, and TOAST
			return true;

		case 'EVERGREEN':
			// Always show (persistent) unless dismissed
			// Typically used for banners that should always be visible
			// Works for BANNER, MODAL, and TOAST
			return true;

		default:
			// Unknown frequency - default to showing (fail open)
			// Warn only in development to avoid console noise in production
			if (process.env.NODE_ENV === 'development') {
				console.warn(`[UpgradePromptContext] Unknown frequency: ${frequency}, defaulting to show`);
			}
			return true;
	}
}

export type UpgradePromptContextValue = {
	activePrompts: ActiveUpgradePrompt[];
	recordEngagement: (promptId: string) => Promise<string | null>;
	hasActiveModal: boolean;
	isLoading: boolean;
	dismissPrompt: (promptId: string) => void;
	markPromptAsShown: (promptId: string, frequency?: string) => void;
};

const UpgradePromptContext = createContext<UpgradePromptContextValue | null>(null);

export type UpgradePromptProviderProps = {
	children: React.ReactNode;
};

/**
 * Context Provider for upgrade prompts.
 * Manages fetching, filtering, and state management for upgrade prompts.
 * Provides prompts to all child components via useUpgradePrompts() hook.
 */
export function UpgradePromptProvider({ children }: UpgradePromptProviderProps) {
	const pathname = usePathname();
	const { user, isInitialLoading } = useAuthenticatedUser();
	const upgradeService = useClientUpgradeService();
	const { addLoader, removeLoader } = useLoadTracker();

	const [prompts, setPrompts] = useState<ActiveUpgradePrompt[]>([]);
	const [dismissedPrompts, setDismissedPrompts] = useState<Set<string>>(new Set());
	const [isLoading, setIsLoading] = useState(true);
	const sessionIdRef = useRef<string>(generateSessionId());
	const hasFetchedRef = useRef(false);

	// Load dismissed/engaged state from session storage
	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		const loadState = () => {
			const dismissed = new Set<string>();
			const engaged = new Set<string>();
			const shown = new Set<string>();

			// Load dismissed prompts
			for (let i = 0; i < sessionStorage.length; i++) {
				const key = sessionStorage.key(i);
				if (key?.startsWith(STORAGE_KEY_PROMPT_DISMISSED)) {
					const promptId = key.replace(STORAGE_KEY_PROMPT_DISMISSED, '');
					dismissed.add(promptId);
				}
				if (key?.startsWith(STORAGE_KEY_PROMPT_ENGAGED)) {
					// Key format: "upgrade_prompt_engaged_{promptId}_{date}"
					// Since promptId is a UUID (36 chars with hyphens), we can extract it more reliably
					const withoutPrefix = key.replace(STORAGE_KEY_PROMPT_ENGAGED, '');
					// Try to extract UUID (36 chars before the last underscore)
					const lastUnderscoreIndex = withoutPrefix.lastIndexOf('_');
					if (lastUnderscoreIndex > 0) {
						const promptId = withoutPrefix.substring(0, lastUnderscoreIndex);
						// Validate it looks like a UUID (contains hyphens and is reasonable length)
						if (promptId && promptId.length >= 30 && promptId.includes('-')) {
							engaged.add(promptId);
						}
					} else {
						// No date separator found, treat entire string as promptId (edge case)
						if (withoutPrefix) {
							engaged.add(withoutPrefix);
						}
					}
				}
				if (key?.startsWith(STORAGE_KEY_PROMPT_SHOWN)) {
					// Key format: "upgrade_prompt_shown_{promptId}" or "upgrade_prompt_shown_{promptId}_{date}"
					const withoutPrefix = key.replace(STORAGE_KEY_PROMPT_SHOWN, '');
					// Check if it has a date suffix (for ONCE_PER_DAY)
					const lastUnderscoreIndex = withoutPrefix.lastIndexOf('_');
					if (lastUnderscoreIndex > 0) {
						// Has date suffix - extract promptId (everything before last underscore)
						const promptId = withoutPrefix.substring(0, lastUnderscoreIndex);
						if (promptId && promptId.length >= 30 && promptId.includes('-')) {
							// Validate it looks like a UUID
							shown.add(promptId);
						}
					} else {
						// No date suffix - entire string is promptId (for ONCE, ONCE_PER_SESSION)
						if (withoutPrefix && withoutPrefix.length >= 30 && withoutPrefix.includes('-')) {
							shown.add(withoutPrefix);
						}
					}
				}
			}

			return { dismissed, engaged, shown };
		};

		const { dismissed } = loadState();
		setDismissedPrompts(dismissed);

		// Listen for dismissal events from modal/banner components
		// Note: This listener is set up in this useEffect to ensure it runs after state is initialized
		// The cleanup function properly removes the listener when component unmounts
		const handleDismissed = (event: Event) => {
			const customEvent = event as CustomEvent<{ promptId: string }>;
			const { promptId } = customEvent.detail;
			setDismissedPrompts((prev) => {
				const updated = new Set(prev);
				updated.add(promptId);
				return updated;
			});
		};

		if (typeof window !== 'undefined') {
			window.addEventListener('upgrade-prompt-dismissed', handleDismissed);

			return () => {
				window.removeEventListener('upgrade-prompt-dismissed', handleDismissed);
			};
		}
	}, []);

	// Fetch active prompts for logged-in users
	useEffect(() => {
		// Wait for auth to finish loading
		if (isInitialLoading) {
			return;
		}

		// Don't fetch if no user or already fetched
		if (!user || hasFetchedRef.current) {
			return;
		}

		// Skip fetching on pages where prompts are never shown (performance optimization)
		const normalizedPath = pathname.split('?')[0].split('#')[0];
		if (normalizedPath === '/upgrade' || normalizedPath.startsWith('/sign-up')) {
			return;
		}

		const fetchPrompts = async () => {
			setIsLoading(true);
			const loader = addLoader();
			try {
				const result = await upgradeService.getActivePrompts(sessionIdRef.current);
				// Ensure result is always an array
				const promptsArray = Array.isArray(result) ? result : [];
				setPrompts(promptsArray);
				hasFetchedRef.current = true;
			} catch (error) {
				console.error('[UpgradePromptContext] Failed to fetch upgrade prompts:', error);
				// Set empty array on error to prevent filter errors
				setPrompts([]);
				hasFetchedRef.current = true; // Mark as fetched even on error to prevent retries
				// Don't show error to user - prompts are optional
			} finally {
				setIsLoading(false);
				removeLoader(loader);
			}
		};

		fetchPrompts();
	}, [user, isInitialLoading, upgradeService, addLoader, removeLoader, pathname]);

	// Handle prompt engagement (when user clicks CTA)
	// Returns engagementId for checkout attribution
	const recordEngagement = useCallback(
		async (promptId: string): Promise<string | null> => {
			try {
				const response = await upgradeService.engagePrompt(promptId, sessionIdRef.current);
				const engagementId = response.engagementId;

				// Store engagementId in sessionStorage for checkout
				if (engagementId && typeof window !== 'undefined') {
					SessionStorageUtil.setItem(STORAGE_KEY_UPGRADE_ENGAGEMENT_ID, engagementId);
				}

				// Mark as engaged
				const engagedKey = `${STORAGE_KEY_PROMPT_ENGAGED}${promptId}_${new Date().toDateString()}`;
				SessionStorageUtil.setItem(engagedKey, 'true');

				return engagementId;
			} catch (error) {
				console.error('Failed to record engagement:', error);
				// Don't block user flow if engagement fails
				return null;
			}
		},
		[upgradeService],
	);

	// Handle prompt dismissal
	const dismissPrompt = useCallback((promptId: string) => {
		const dismissedKey = `${STORAGE_KEY_PROMPT_DISMISSED}${promptId}`;
		SessionStorageUtil.setItem(dismissedKey, 'true');
		setDismissedPrompts((prev) => {
			const updated = new Set(prev);
			updated.add(promptId);
			return updated;
		});
	}, []);

	// Mark prompt as shown (for frequency tracking)
	// Handles ONCE, ONCE_PER_SESSION, and ONCE_PER_DAY frequencies
	// Updates sessionStorage immediately (filtering checks sessionStorage directly, not React state)
	const markPromptAsShown = useCallback((promptId: string, frequency?: string) => {
		// Update sessionStorage immediately for persistence
		// The filtering logic checks sessionStorage directly, so this won't cause re-filtering
		if (frequency === 'ONCE_PER_DAY') {
			// For ONCE_PER_DAY, store with today's date so it resets at midnight
			const todayKey = `${STORAGE_KEY_PROMPT_SHOWN}${promptId}_${new Date().toDateString()}`;
			SessionStorageUtil.setItem(todayKey, 'true');
		} else {
			// For ONCE and ONCE_PER_SESSION, store without date
			const shownKey = `${STORAGE_KEY_PROMPT_SHOWN}${promptId}`;
			SessionStorageUtil.setItem(shownKey, 'true');
		}

		// Update React state for UI reactivity (but filtering doesn't depend on this for ONCE/ONCE_PER_SESSION/ONCE_PER_DAY)
	}, []);

	// Filter prompts that should be shown (by frequency/dismissal AND route)
	const activePrompts = useMemo(() => {
		// Defensive check: ensure prompts is always an array
		if (!Array.isArray(prompts)) {
			return [];
		}

		return prompts.filter((prompt) => {
			// First check frequency/dismissal logic
			const shouldShowByFrequency = shouldShowPrompt(prompt, dismissedPrompts);
			if (!shouldShowByFrequency) {
				return false;
			}

			// Then check route-based filtering
			const promptType = normalizePromptType(prompt.prompt.promptType);
			const shouldShowByRoute = shouldShowPromptOnPage(promptType, pathname);
			if (!shouldShowByRoute) {
				return false;
			}

			return true;
		});
	}, [prompts, dismissedPrompts, pathname]);

	// Check if there's an active modal prompt
	const hasActiveModal = useMemo(() => activePrompts.some((p) => p.prompt.promptType === 'MODAL'), [activePrompts]);

	const value: UpgradePromptContextValue = useMemo(
		() => ({
			activePrompts,
			recordEngagement,
			hasActiveModal,
			isLoading,
			dismissPrompt,
			markPromptAsShown,
		}),
		[activePrompts, recordEngagement, hasActiveModal, isLoading, dismissPrompt, markPromptAsShown],
	);

	return <UpgradePromptContext.Provider value={value}>{children}</UpgradePromptContext.Provider>;
}

/**
 * Hook to access upgrade prompts context.
 * Must be used within UpgradePromptProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { activePrompts, recordEngagement } = useUpgradePrompts();
 *   const bannerPrompt = activePrompts.find(p => p.prompt.promptType === 'BANNER');
 *   // ...
 * }
 * ```
 */
export function useUpgradePrompts(): UpgradePromptContextValue {
	const context = useContext(UpgradePromptContext);
	if (!context) {
		throw new Error('useUpgradePrompts must be used within UpgradePromptProvider');
	}
	return context;
}
